<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import GbButton from '@/components/GbButton.vue'
import SkillStatements from '@/presentlevels/no-ai/SkillStatements.vue'
import { usePresentLevelsWizardStore } from '@/presentlevels/no-ai/present_levels_wizard.store'
import { useUserStore } from '@/authentication/user.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useRedirectIfUnauthorized } from '@/authentication/useRedirectIfUnauthorized'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { RESULTS_EVENT_NAMES } from '@/presentlevels/no-ai/event_names'
import {
  getAreasOfNeed,
  getImpactBySubject,
  getPresentLevelStudentStatement,
  getSkillBySubject,
  getSkillForEffectStatement,
  getSkillTypes,
  returnGradeFromEnrolledGrade
} from '@/presentlevels/api_v1.js'
import { betaOptIn } from '@/util/beta_opt_in'
import type { Skill, StudentStatement } from '@/presentlevels/no-ai/skill_statement_types'

type ResultsState = 'init' | 'fetchingStudent' | 'fetchingStatements' | 'studentError' | 'statementError' | 'done'

type SubjectSkill = Skill & {
  skill_order: number | string
}

type SkillWithStatements = SubjectSkill & {
  studentStatements: StudentStatement[]
}

type SkillsResponse = {
  skills: SubjectSkill[]
}

type AreaOfNeed = {
  label: string
  value: string
}

type StudentStatementResponse = {
  subject: string
  statements: StudentStatement[]
  student: {
    user_uuid: string
    student_name: string
    enrolled_grade_level: string
    selected_subjects: string[]
    updated_at: string
  }
}

const BEHAVIOR = 'Behavior'

const props = defineProps<{
  uuid?: string
  subject?: string
}>()

const route = useRoute()
const router = useRouter()
const wizardStore = usePresentLevelsWizardStore()
const { licenseSubType } = storeToRefs(useUserStore())
const { profileId } = storeToRefs(useContentProfileStore())
const { selVisibility, iodVisibility, effectsVisibility } = storeToRefs(useVisibilityStore())
const { redirectIfUnauthorized } = useRedirectIfUnauthorized()
const skillTypes = getSkillTypes()

const currentState = ref<ResultsState>('init')
const currentSubject = ref('')
const nextSubject = ref('')
const studentName = ref('')
const studentGrade = ref('')
const skills = ref<SkillWithStatements[]>([])
const impacts = ref<SkillWithStatements[]>([])

const analytics = getAnalyticsTracker().getChildContext({
  plw_area_of_need: currentSubject
})

function areasOfNeed(): AreaOfNeed[] {
  return getAreasOfNeed({ selBlocked: !selVisibility.value, iodSubject: false })
}

function areaLabel(subject: string): string {
  return areasOfNeed().find((area) => area.value === subject)?.label ?? subject
}

const currentSubjectLabel = computed(() => areaLabel(currentSubject.value))
const nextSubjectLabel = computed(() => areaLabel(nextSubject.value))
const isBehavior = computed(() => Boolean(studentName.value) && props.subject === BEHAVIOR)
const isImpactSectionShown = computed(() => impacts.value.length > 0 && (iodVisibility.value || effectsVisibility.value))

function withBetaOptIn() {
  return betaOptIn(route.query)
}

function capitalized(subject: string): string {
  return subject.charAt(0).toUpperCase() + subject.substring(1)
}

let latestLoad = 0

function isStale(load: number): boolean {
  return load !== latestLoad
}

function bySkillOrder(first: SubjectSkill, second: SubjectSkill): number {
  return Number(first.skill_order) - Number(second.skill_order)
}

function withStudentStatements(skillsResponse: SkillsResponse, studentStatements: StudentStatement[]): SkillWithStatements[] {
  return [...skillsResponse.skills].sort(bySkillOrder).map((skill) => ({
    ...skill,
    studentStatements: studentStatements.filter((statement) => statement.skill_uuid === skill.skill_uuid)
  }))
}

function selectedSubjectsInAreaOrder(selectedSubjects: string[]): string[] {
  return getAreasOfNeed()
    .map((area: AreaOfNeed) => area.value)
    .filter((subject: string) => selectedSubjects.includes(subject))
}

function subjectAfter(subject: string, subjects: string[]): string {
  const subjectIndex = subjects.indexOf(subject)
  const hasNextSubject = subjectIndex >= 0 && subjectIndex < subjects.length - 1
  if (!hasNextSubject) {
    return ''
  }
  return subjects[subjectIndex + 1]
}

function rememberStudent(uuid: string, data: StudentStatementResponse, selectedSubjects: string[]) {
  studentGrade.value = data.student.enrolled_grade_level
  studentName.value = data.student.student_name
  wizardStore.lastUpdate = data.student.updated_at
  wizardStore.setBreadcrumbsInfo({ uuid, availableStages: selectedSubjects })
  wizardStore.setStudentInfo({ studentName: studentName.value, enrolledGrade: studentGrade.value, areasOfNeed: selectedSubjects })
}

// Clark County's effect statements stand in for the impact of disability statements.
function fetchImpactsOrEffects(subject: string, enrolledGrade: string): Promise<SkillsResponse> {
  if (effectsVisibility.value) {
    return getSkillForEffectStatement(subject, returnGradeFromEnrolledGrade(enrolledGrade))
  }
  return getImpactBySubject(subject, licenseSubType.value, profileId.value)
}

async function fetchStudentSkills(uuid: string, load: number): Promise<[SkillsResponse, SkillsResponse, StudentStatement[]] | undefined> {
  currentState.value = 'fetchingStudent'
  try {
    const data: StudentStatementResponse = await getPresentLevelStudentStatement(uuid, currentSubject.value)
    if (isStale(load)) {
      return undefined
    }
    await redirectIfUnauthorized(data.student.user_uuid, { name: 'PresentLevelsNewWizard', query: withBetaOptIn() })
    if (isStale(load)) {
      return undefined
    }
    currentState.value = 'fetchingStatements'

    if (!currentSubject.value) {
      router.replace({ name: 'PresentlevelsSubjectResults', params: { uuid, subject: data.subject }, query: withBetaOptIn() })
      return undefined
    }
    const selectedSubjects = selectedSubjectsInAreaOrder(data.student.selected_subjects)
    nextSubject.value = subjectAfter(currentSubject.value, selectedSubjects)
    rememberStudent(uuid, data, selectedSubjects)
    return Promise.all([
      getSkillBySubject(data.subject, licenseSubType.value, profileId.value),
      fetchImpactsOrEffects(data.subject, data.student.enrolled_grade_level),
      data.statements
    ])
  } catch (error) {
    if (isStale(load)) {
      return undefined
    }
    currentState.value = 'studentError'
    console.log(error)
    return undefined
  }
}

async function fetchBrowseSkills(): Promise<[SkillsResponse, SkillsResponse, StudentStatement[]]> {
  currentState.value = 'fetchingStatements'
  return Promise.all([
    getSkillBySubject(currentSubject.value, licenseSubType.value, profileId.value),
    getImpactBySubject(currentSubject.value, licenseSubType.value, profileId.value),
    []
  ])
}

async function showSkills(skillsFetch: Promise<[SkillsResponse, SkillsResponse, StudentStatement[]] | undefined>, load: number) {
  try {
    const fetchedSkills = await skillsFetch
    if (!fetchedSkills || isStale(load)) {
      return
    }
    const [skillData, impactData, studentStatements] = fetchedSkills
    if (!skillData) {
      currentState.value = 'statementError'
      return
    }
    skills.value = withStudentStatements(skillData, studentStatements)
    impacts.value = withStudentStatements(impactData, studentStatements)
    currentState.value = 'done'
  } catch (error) {
    if (isStale(load)) {
      return
    }
    currentState.value = 'statementError'
    console.error(error)
  }
}

// A lowercase subject in the URL is rewritten to the capitalized one; the new subject then loads the page.
function isRewritingBrowseSubject(): boolean {
  if (props.subject === currentSubject.value) {
    return false
  }
  router.replace({ name: 'PresentlevelsBrowse', params: { subject: currentSubject.value }, query: withBetaOptIn() })
  return true
}

async function init() {
  const load = ++latestLoad
  currentState.value = 'init'
  currentSubject.value = capitalized(props.subject ?? '')

  if (props.uuid) {
    await showSkills(fetchStudentSkills(props.uuid, load), load)
    return
  }
  if (isRewritingBrowseSubject()) {
    return
  }
  await showSkills(fetchBrowseSkills(), load)
}

function goToNextStep() {
  if (nextSubject.value) {
    analytics.sendEvent(RESULTS_EVENT_NAMES.CLICKED_SELECT_NEXT_SUBJECT)
    router.push({ name: 'PresentlevelsSubjectResults', params: { uuid: props.uuid, subject: nextSubject.value }, query: withBetaOptIn() })
    return
  }
  analytics.sendEvent(RESULTS_EVENT_NAMES.CLICKED_GENERATE_DRAFT, {
    statement_uuid: props.uuid,
    enrolled_grade: studentGrade.value,
    area_of_need: currentSubject.value
  })
  router.push({ name: 'PresentlevelsConclusion', params: { uuid: props.uuid }, query: withBetaOptIn() })
}

watch(() => [props.uuid, props.subject], init, { immediate: true })
</script>

<template>
  <div>
    <h4 v-if="currentState === 'fetchingStudent'" class="text-center text-text-xl">
      Loading student information...
    </h4>
    <h4 v-if="currentState === 'fetchingStatements'" class="text-center text-text-xl">
      Generating statements...
    </h4>
    <h3 v-if="currentState === 'studentError'" class="bg-red-100 text-center text-display-sm">
      Failed to load student information! Please wait a moment and refresh this page.
    </h3>
    <h3 v-if="currentState === 'statementError'" class="bg-red-100 text-center text-display-sm">
      Failed to generate statement information! Please wait a moment and refresh this page.
    </h3>

    <template v-if="currentState === 'done'">
      <h2 v-if="isBehavior" class="mb-2.5 text-center text-display-md leading-9">
        Describe the specific
        <span class="italic text-blue-400">Behavior</span>
        that is most relevant to
        <span class="fs-block italic text-blue-400">{{ studentName }}'s</span> present levels.
      </h2>
      <h2 v-else-if="subject" class="mb-2.5 text-center text-display-md leading-9">
        Identify Area(s) of Strength or Need
        <br>
        <template v-if="studentName">
          for
          <span class="fs-block italic text-blue-400">{{ studentName }}</span>
        </template>
        in
        <span class="italic text-blue-400">{{ currentSubjectLabel }}</span>
      </h2>

      <SkillStatements
        v-for="skill in skills"
        :key="skill.skill_uuid"
        :subject="currentSubject"
        :skill="skill"
        :student-name="studentName"
        :student-grade="studentGrade"
        :student-statements="skill.studentStatements"
        :present-level-uuid="uuid"
        :is-locked="skill.locked"
      />

      <template v-if="isImpactSectionShown">
        <h2 class="mb-2.5 mt-10 text-center text-display-md leading-9">
          <span v-if="effectsVisibility">Effect Statement</span>
          <span v-else>Select Impact of Disability Statement(s)</span>
          <br>
          <template v-if="studentName">
            for
            <span class="fs-block italic text-blue-400">{{ studentName }}</span>
          </template>
          in
          <span class="italic text-blue-400">{{ currentSubjectLabel }}</span>
        </h2>

        <SkillStatements
          v-for="impact in impacts"
          :key="impact.skill_uuid"
          :subject="currentSubject"
          :skill="impact"
          :skill-type="skillTypes.IMPACT"
          :student-name="studentName"
          :student-grade="studentGrade"
          :student-statements="impact.studentStatements"
          :present-level-uuid="uuid"
          is-multi-select
          :is-effects-template="effectsVisibility"
          :is-locked="impact.locked"
        />
      </template>

      <div class="text-center">
        <GbButton
          v-if="uuid"
          id="plDraftButton"
          gb-theme="bluePrimary"
          class="mt-12 whitespace-normal break-words !px-6 !py-4 !text-display-sm !leading-9"
          @click="goToNextStep"
        >
          <template v-if="nextSubject">Select {{ nextSubjectLabel }} Statements</template>
          <template v-else>Generate draft present levels</template>
        </GbButton>
      </div>
    </template>
  </div>
</template>
